import { 
  OnboardingProject, 
  OnboardingTask, 
  OnboardingTemplate, 
  OnboardingMetrics, 
  AgenticReminder,
  OnboardingAnalytics,
  OnboardingPlaybook
} from './types';

export const mockProjects: OnboardingProject[] = [
  {
    id: 'proj-001',
    name: 'Enterprise CRM Implementation',
    type: 'implementation',
    status: 'active',
    progress: 65,
    startDate: '2024-01-15',
    targetDate: '2026-04-30',
    clientName: 'TechCorp Solutions',
    projectManager: 'Sarah Chen',
    teamMembers: ['Mike Johnson', 'Lisa Wang', 'David Rodriguez', 'Emma Thompson'],
    priority: 'high',
    healthScore: 85,
    lastActivity: '2024-01-20T14:30:00Z'
  },
  {
    id: 'proj-002',
    name: 'Cloud Migration - Phase 1',
    type: 'migration',
    status: 'planning',
    progress: 25,
    startDate: '2024-02-01',
    targetDate: '2026-06-15',
    clientName: 'Global Manufacturing Inc',
    projectManager: 'Alex Kumar',
    teamMembers: ['Jennifer Lee', 'Robert Kim', 'Maria Garcia'],
    priority: 'critical',
    healthScore: 92,
    lastActivity: '2024-01-19T16:45:00Z'
  },
  {
    id: 'proj-003',
    name: 'API Integration Suite',
    type: 'integration',
    status: 'at-risk',
    progress: 40,
    startDate: '2023-12-01',
    targetDate: '2026-03-15',
    clientName: 'FinTech Innovations',
    projectManager: 'Jordan Smith',
    teamMembers: ['Chris Wilson', 'Amanda Brown', 'Kevin Zhang'],
    priority: 'medium',
    healthScore: 68,
    lastActivity: '2024-01-18T11:20:00Z'
  }
];

export const mockTasks: OnboardingTask[] = [
  {
    id: 'task-001',
    projectId: 'proj-001',
    title: 'Complete stakeholder requirements gathering',
    description: 'Conduct interviews with key stakeholders to finalize CRM requirements',
    type: 'documentation',
    status: 'in-progress',
    assignee: 'Mike Johnson',
    dueDate: '2026-01-25',
    priority: 'high',
    dependencies: [],
    estimatedHours: 16,
    actualHours: 12,
    completionPercentage: 75,
    tags: ['requirements', 'stakeholders', 'discovery'],
    attachments: [
      {
        id: 'att-001',
        name: 'Stakeholder Interview Template.docx',
        type: 'template',
        url: '/attachments/stakeholder-template.docx',
        uploadedBy: 'Sarah Chen',
        uploadedAt: '2024-01-15T09:00:00Z',
        size: 245760
      }
    ],
    comments: [
      {
        id: 'comm-001',
        author: 'Sarah Chen',
        content: 'Great progress on the stakeholder interviews. Make sure to capture the integration requirements clearly.',
        timestamp: '2024-01-20T10:30:00Z',
        type: 'comment'
      }
    ]
  },
  {
    id: 'task-002',
    projectId: 'proj-001',
    title: 'Set up development environment',
    description: 'Configure CRM development environment with proper access controls',
    type: 'setup',
    status: 'completed',
    assignee: 'Lisa Wang',
    dueDate: '2026-01-20',
    priority: 'medium',
    dependencies: ['task-001'],
    estimatedHours: 8,
    actualHours: 6,
    completionPercentage: 100,
    tags: ['setup', 'environment', 'access'],
    attachments: [],
    comments: []
  },
  {
    id: 'task-003',
    projectId: 'proj-002',
    title: 'Cloud architecture review',
    description: 'Review and approve cloud migration architecture design',
    type: 'review',
    status: 'pending',
    assignee: 'Jennifer Lee',
    dueDate: '2026-02-05',
    priority: 'critical',
    dependencies: [],
    estimatedHours: 12,
    completionPercentage: 0,
    tags: ['architecture', 'cloud', 'review'],
    attachments: [],
    comments: []
  }
];

export const mockTemplates: OnboardingTemplate[] = [
  {
    id: 'template-001',
    name: 'Standard CRM Implementation',
    description: 'Comprehensive template for CRM system implementations',
    projectType: 'implementation',
    phases: [
      {
        id: 'phase-001',
        name: 'Discovery & Planning',
        description: 'Initial project setup and requirements gathering',
        order: 1,
        estimatedDuration: 14,
        prerequisites: [],
        deliverables: ['Requirements Document', 'Project Charter', 'Resource Plan'],
        tasks: [],
        milestones: [
          {
            id: 'milestone-001',
            name: 'Requirements Sign-off',
            description: 'Client approval of requirements document',
            dueDate: '2026-02-15',
            status: 'pending',
            criteria: ['Requirements document reviewed', 'Stakeholder approval obtained'],
            approver: 'Client Project Sponsor'
          }
        ]
      }
    ],
    estimatedDuration: 90,
    requiredRoles: ['Project Manager', 'Business Analyst', 'Technical Lead', 'Developer'],
    tags: ['crm', 'implementation', 'standard']
  }
];

export const mockMetrics: OnboardingMetrics = {
  totalProjects: 12,
  activeProjects: 8,
  completedThisMonth: 3,
  averageCompletionTime: 85,
  onTimeDeliveryRate: 92,
  clientSatisfactionScore: 4.6,
  teamUtilization: 78,
  upcomingDeadlines: 15
};

export const mockReminders: AgenticReminder[] = [
  {
    id: 'reminder-001',
    projectId: 'proj-001',
    taskId: 'task-001',
    type: 'deadline',
    priority: 'high',
    title: 'Stakeholder Requirements Due Soon',
    message: 'The stakeholder requirements gathering task is due in 2 days. Consider scheduling follow-up meetings if needed.',
    suggestedActions: [
      'Schedule additional stakeholder meetings',
      'Request deadline extension if needed',
      'Prioritize remaining interviews'
    ],
    dueDate: '2026-01-25',
    createdAt: '2024-01-23T09:00:00Z',
    acknowledged: false,
    source: 'pmsensei'
  },
  {
    id: 'reminder-002',
    projectId: 'proj-003',
    type: 'risk-alert',
    priority: 'critical',
    title: 'Project Health Score Declining',
    message: 'API Integration Suite project health has dropped to 68%. Multiple tasks are behind schedule.',
    suggestedActions: [
      'Review resource allocation',
      'Identify and address blockers',
      'Consider scope adjustment',
      'Schedule team standup meeting'
    ],
    createdAt: '2024-01-22T15:30:00Z',
    acknowledged: false,
    source: 'system'
  }
];

export const mockAnalytics: OnboardingAnalytics = {
  projectHealth: {
    healthy: 6,
    atRisk: 2,
    critical: 0
  },
  taskDistribution: {
    pending: 24,
    inProgress: 18,
    completed: 45,
    overdue: 3
  },
  teamWorkload: [
    { name: 'Sarah Chen', utilization: 85, activeTasks: 6 },
    { name: 'Alex Kumar', utilization: 92, activeTasks: 8 },
    { name: 'Jordan Smith', utilization: 78, activeTasks: 5 },
    { name: 'Mike Johnson', utilization: 88, activeTasks: 7 }
  ],
  upcomingMilestones: [
    {
      id: 'milestone-002',
      name: 'Phase 1 Completion',
      description: 'Complete all Phase 1 deliverables',
      dueDate: '2026-02-01',
      status: 'pending',
      criteria: ['All tasks completed', 'Quality review passed'],
      approver: 'Project Sponsor'
    }
  ],
  recentActivity: [
    {
      type: 'task-completed',
      description: 'Development environment setup completed',
      timestamp: '2024-01-20T14:30:00Z',
      user: 'Lisa Wang'
    },
    {
      type: 'comment-added',
      description: 'New comment on stakeholder requirements task',
      timestamp: '2024-01-20T10:30:00Z',
      user: 'Sarah Chen'
    }
  ]
};

export const mockPlaybooks: OnboardingPlaybook[] = [
  {
    id: 'playbook-001',
    name: 'Overdue Task Escalation',
    description: 'Automatically escalate tasks that are overdue by more than 2 days',
    projectType: 'all',
    triggers: ['task-overdue'],
    conditions: { overdueDays: { $gt: 2 } },
    actions: [
      {
        id: 'action-001',
        type: 'send-reminder',
        parameters: {
          recipient: 'task-assignee',
          template: 'overdue-task-reminder',
          escalateTo: 'project-manager'
        }
      }
    ],
    isActive: true,
    createdBy: 'System',
    lastModified: '2024-01-15T12:00:00Z'
  }
];
